import { MeasurementUnitSymbol } from "@safetyculture/s12-apis-connect-web/s12/common/measurement_unit_pb";
import { UpdateTrialEndSignal } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/workflows_pb";
import {
  ArchiveAlexandriaDocumentsRequest,
  ArchiveAlexandriaDocumentsResponse,
  ArchiveDocumentByOwnerRequest,
  ArchiveDocumentByOwnerResponse,
  BulkAddUsersToGroupsRequest,
  BulkAddUsersToGroupsResponse,
  BulkChangeEmailAddressRequest,
  BulkChangeEmailAddressResponse,
  BulkCopyTemplatePermissionAndAutoShareRequest,
  BulkCopyTemplatePermissionAndAutoShareResponse,
  BulkCreateDemoAssetReadingsRequest,
  BulkCreateDemoAssetsRequest,
  BulkCreateDemoOrgDataRequest,
  BulkCreateDemoOrgDataResponse,
  BulkMoveGRSRequest,
  BulkMoveGRSResponse,
  BulkRestoreArchivedInspectionsRequest,
  BulkRestoreArchivedInspectionsResponse,
  BulkRestoreDeletedInspectionsRequest,
  BulkRestoreDeletedInspectionsResponse,
  BulkTransferUsersRequest,
  BulkTransferUsersResponse,
  BulkUpdateDocumentsContextRequest,
  BulkUpdateDocumentsContextResponse,
  BulkUpdateInspectionTitleRequest,
  BulkUpdateInspectionTitleResponse,
  BulkUpdateOrgInspectionsTitle,
  CloneOrganisationSignal,
  ExecuteWorkflowByNameRequest,
  ExecuteWorkflowByNameResponse,
  GetWorkflowStatusRequest,
  GetWorkflowStatusResponse,
  InspectionIDList,
  ListWorkflowExecutionsRequest,
  ListWorkflowExecutionsResponse,
  ProvisionSupportUserRequest,
  ProvisionSupportUserResponse,
  QueryWorkflowRequest,
  QueryWorkflowResponse,
  Region,
  ResetOrgTemplatesAccessRequest,
  ResetOrgTemplatesAccessResponse,
  ResetWorkflowExecutionRequest,
  ResetWorkflowExecutionResponse,
  SCEmployee,
  SignalSubscriptionsWorkflowRequest,
  SignalSubscriptionsWorkflowResponse,
  SignalWorkflowRequest,
  SignalWorkflowResponse,
  TerminateSupportWorkflowExecutionRequest,
  TerminateSupportWorkflowExecutionResponse,
  UnblockMigrationSignal,
  WorkflowClientType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { toUUID, validateUUID } from "../../helpers/s12-helpers";
import { supportServiceConnectClient } from "../index";
import { RequestMetadata } from "api/types";
import {
  AssetProfile,
  AssetReadingConfig,
  Odometer,
  Usage,
} from "components/org-display/tabs/data-governance-actions/data-loader/data-loader-dialog";
import {
  DEFAULT_TABLE_PAGINATION,
  DEFAULT_WORKFLOW_CLIENT_TYPE,
} from "constants/index";
import { WORKFLOW_CLIENT_TYPES } from "constants/workflow-client-types";

// General utilities for workflows

export function stringToUint8Array(str: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

export function uint8ArrayToString(uint8Array: Uint8Array): string {
  const decoder = new TextDecoder();
  return decoder.decode(uint8Array);
}

type ListWorkflowExecutionsParams = {
  pageSize?: number;
  workflowType: string;
  nextPageToken?: Uint8Array;
  clientType?: string;
};

export const listWorkflowExecutions = async (
  {
    pageSize = DEFAULT_TABLE_PAGINATION,
    workflowType,
    nextPageToken,
    clientType = DEFAULT_WORKFLOW_CLIENT_TYPE,
  }: ListWorkflowExecutionsParams,
  headers: Record<RequestMetadata, string>
): Promise<ListWorkflowExecutionsResponse> => {
  const req = new ListWorkflowExecutionsRequest({
    workflowType,
    clientType: mapClientTypeToEnum(clientType),
    pageSize,
  });

  if (nextPageToken) {
    req.pageToken = nextPageToken;
  }

  return supportServiceConnectClient.listWorkflowExecutions(req, {
    headers,
  });
};

type TerminateSupportWorkflowExecutionParams = {
  workflowId: string;
  workflowTaskFinishEventId?: bigint;
  reason?: string;
};

export const terminateSupportWorkflowExecution = async (
  { workflowId, reason }: TerminateSupportWorkflowExecutionParams,
  headers: Record<RequestMetadata, string>
): Promise<TerminateSupportWorkflowExecutionResponse> => {
  const req = new TerminateSupportWorkflowExecutionRequest({
    workflowId,
  });

  if (reason) {
    req.reason = reason;
  }

  return supportServiceConnectClient.terminateSupportWorkflowExecution(req, {
    headers,
  });
};

type ResetWorkflowExecutionParams = {
  workflowId: string;
  workflowTaskFinishEventId?: bigint;
  reason?: string;
  scEmployee: SCEmployee;
};
export const resetWorkflowExecution = async (
  {
    workflowId,
    workflowTaskFinishEventId,
    reason,
    scEmployee,
  }: ResetWorkflowExecutionParams,
  headers: Record<RequestMetadata, string>
): Promise<ResetWorkflowExecutionResponse> => {
  const req = new ResetWorkflowExecutionRequest({
    workflowId,
    workflowTaskFinishEventId: workflowTaskFinishEventId || BigInt(3),
    namespace: "default",
    scEmployee,
  });

  if (reason) {
    req.reason = reason;
  }

  return supportServiceConnectClient.resetWorkflowExecution(req, {
    headers,
  });
};

type QueryWorkflowParams = {
  workflowId: string;
  queryType: string;
  runId: string;
};

export const queryWorkflow = async (
  { workflowId, queryType, runId }: QueryWorkflowParams,
  headers: Record<RequestMetadata, string>
): Promise<QueryWorkflowResponse> => {
  const req = new QueryWorkflowRequest({
    workflowId,
    queryType,
    runId,
  });

  return supportServiceConnectClient.queryWorkflow(req, {
    headers,
  });
};

type SignalWorkflowParams = {
  workflowId: string;
  signalName: string;
  signalArgumentsValue?: UnblockMigrationSignal | CloneOrganisationSignal;
};

type SignalSubscriptionsWorkflowParams = {
  workflowId: string;
  signalName: string;
  signalArgumentsValue?: UpdateTrialEndSignal;
};

export const signalWorkflow = async (
  { workflowId, signalName, signalArgumentsValue }: SignalWorkflowParams,
  headers: Record<RequestMetadata, string>
): Promise<SignalWorkflowResponse> => {
  const req = new SignalWorkflowRequest({
    workflowId,
    signalName,
  });

  if (signalArgumentsValue instanceof UnblockMigrationSignal) {
    req.signalArguments = {
      value: signalArgumentsValue,
      case: "unblockMigration",
    };
  } else if (signalArgumentsValue instanceof CloneOrganisationSignal) {
    req.signalArguments = {
      value: signalArgumentsValue,
      case: "extendCloneLifetime",
    };
  }
  return supportServiceConnectClient.signalWorkflow(req, {
    headers,
  });
};

export const signalSubscriptionWorkflow = async (
  {
    workflowId,
    signalName,
    signalArgumentsValue,
  }: SignalSubscriptionsWorkflowParams,
  headers: Record<RequestMetadata, string>
): Promise<SignalSubscriptionsWorkflowResponse> => {
  const req = new SignalSubscriptionsWorkflowRequest({
    workflowId,
    signalName,
  });

  if (signalArgumentsValue instanceof UpdateTrialEndSignal) {
    req.signalArguments = {
      value: signalArgumentsValue,
      case: "updateTrialEnd",
    };
  }
  return supportServiceConnectClient.signalSubscriptionsWorkflow(req, {
    headers,
  });
};

type GetWorkflowStatusParams = {
  workflowId: string;
  queryType: string;
  clientType?: string;
};

export const getWorkflowStatus = async (
  {
    workflowId,
    queryType,
    clientType = DEFAULT_WORKFLOW_CLIENT_TYPE,
  }: GetWorkflowStatusParams,
  headers: Record<RequestMetadata, string>
): Promise<GetWorkflowStatusResponse> => {
  const req = new GetWorkflowStatusRequest({
    workflowId,
    queryType,
    namespace: "default",
    clientType: mapClientTypeToEnum(clientType),
  });

  return supportServiceConnectClient.getWorkflowStatus(req, {
    headers,
  });
};

// Business logic workflows

type BulkUpdateInspectionTitleParams = {
  inspectionList?: string[];
  region: Region;
  employeeName: string;
  employeeEmail: string;
  inspectionTitleRequestReference: string;
  templateId?: string;
  inspectionOrgId?: string;
};

export const bulkUpdateInspectionTitle = async (
  {
    inspectionList,
    region,
    employeeName,
    employeeEmail,
    inspectionTitleRequestReference,
    templateId,
    inspectionOrgId,
  }: BulkUpdateInspectionTitleParams,
  headers: Record<RequestMetadata, string>
): Promise<BulkUpdateInspectionTitleResponse> => {
  if (!inspectionList && !(templateId && inspectionOrgId)) {
    throw new Error(
      "Invalid parameters: Either 'inspectionList' or both 'templateId' and 'inspectionOrgId' must be provided."
    );
  }
  const req = new BulkUpdateInspectionTitleRequest({
    region,
    scEmployee: new SCEmployee({
      name: employeeName,
      email: employeeEmail,
    }),
    bulkInspectionTileRequestReference: inspectionTitleRequestReference,
  });

  if (inspectionList) {
    req.inspectionsIdentifier = {
      value: new InspectionIDList({
        inspectionIds: inspectionList,
      }),
      case: "inspectionIdList",
    };
  }

  if (templateId && inspectionOrgId) {
    req.inspectionsIdentifier = {
      value: new BulkUpdateOrgInspectionsTitle({
        templateId: templateId,
        inspectionOrgId: inspectionOrgId,
      }),
      case: "bulkUpdateOrgInspections",
    };
  }

  return supportServiceConnectClient.bulkUpdateInspectionTitle(req, {
    headers,
  });
};

type BulkRestoreArchivedInspectionsParams = {
  inspectionList: string[];
  region: Region;
};

export const bulkRestoreArchivedInspections = async (
  { inspectionList, region }: BulkRestoreArchivedInspectionsParams,
  headers: Record<RequestMetadata, string>
): Promise<BulkRestoreArchivedInspectionsResponse> => {
  const req = new BulkRestoreArchivedInspectionsRequest({
    inspectionIds: inspectionList,
    region,
  });

  return supportServiceConnectClient.bulkRestoreArchivedInspections(req, {
    headers,
  });
};

type ArchiveAlexandriaDocuementsParams = {
  documentList: string[];
  region: Region;
};

export const archiveAlexandriaDocuments = async (
  { documentList, region }: ArchiveAlexandriaDocuementsParams,
  headers: Record<RequestMetadata, string>
): Promise<ArchiveAlexandriaDocumentsResponse> => {
  const req = new ArchiveAlexandriaDocumentsRequest({
    documentIds: documentList,
    region,
  });

  return supportServiceConnectClient.archiveAlexandriaDocuments(req, {
    headers,
  });
};

type ArchiveDocumentsByOwnerParams = {
  userId: string;
  includeInspections: boolean;
  includeTemplates: boolean;
  region: Region;
};

export const archiveDocumentsByOwner = async (
  {
    userId,
    includeInspections,
    includeTemplates,
    region,
  }: ArchiveDocumentsByOwnerParams,
  headers: Record<RequestMetadata, string>
): Promise<ArchiveDocumentByOwnerResponse> => {
  const req = new ArchiveDocumentByOwnerRequest({
    userId,
    includeInspections,
    includeTemplates,
    region,
  });

  return supportServiceConnectClient.archiveDocumentByOwner(req, {
    headers,
  });
};

type BulkRestoreDeletedInspectionParams = {
  inspectionList: string[];
  region: Region;
};

export const bulkRestoreDeletedInspections = async (
  { inspectionList, region }: BulkRestoreDeletedInspectionParams,
  headers: Record<RequestMetadata, string>
): Promise<BulkRestoreDeletedInspectionsResponse> => {
  const req = new BulkRestoreDeletedInspectionsRequest({
    documentIds: inspectionList,
    region,
  });

  return supportServiceConnectClient.bulkRestoreDeletedInspections(req, {
    headers,
  });
};

type BulkCopyTemplatePermissionsParams = {
  orgId: string;
  SourceTemplateId: string;
  destinationTemplateIds: string[];
  allTemplates: boolean;
  region: Region;
};

export const bulkCopyTemplatePermissions = async (
  {
    orgId: OrgId,
    SourceTemplateId,
    destinationTemplateIds: DestinationTemplateIds,
    allTemplates: AllTemplates,
    region: Region,
  }: BulkCopyTemplatePermissionsParams,
  headers: Record<RequestMetadata, string>
): Promise<BulkCopyTemplatePermissionAndAutoShareResponse> => {
  const req = new BulkCopyTemplatePermissionAndAutoShareRequest({
    orgId: OrgId,
    sourceTemplateId: SourceTemplateId,
    destinationTemplateIds: DestinationTemplateIds,
    allTemplates: AllTemplates,
    region: Region,
  });

  return supportServiceConnectClient.bulkCopyTemplatePermissionAndAutoShare(
    req,
    {
      headers,
    }
  );
};

type BulkMoveGRSParams = {
  source_org_id: string;
  target_org_id: string;
  region: Region;
  global_response_set_ids: string[];
  sc_employee: SCEmployee;
};

export const bulkMoveGRS = async (
  {
    source_org_id,
    target_org_id,
    global_response_set_ids,
    region,
    sc_employee,
  }: BulkMoveGRSParams,
  headers: Record<RequestMetadata, string>
): Promise<BulkMoveGRSResponse> => {
  const req = new BulkMoveGRSRequest({
    sourceOrgId: source_org_id,
    targetOrgId: target_org_id,
    globalResponseSetIds: global_response_set_ids,
    region,
    scEmployee: sc_employee,
  });

  return supportServiceConnectClient.bulkMoveGRS(req, {
    headers,
  });
};

type ExecuteWorkflowByNameParams = {
  workflowName: string;
  payload: string;
  employeeName: string;
  employeeEmail: string;
  clientType?: string;
};

// Helper function to map UI string values to WorkflowClientType enum
const mapClientTypeToEnum = (clientType: string): WorkflowClientType => {
  switch (clientType) {
    case WORKFLOW_CLIENT_TYPES.GOVERNANCE:
      return WorkflowClientType.GOVERNANCE;
    case WORKFLOW_CLIENT_TYPES.SUBSCRIPTIONS:
      return WorkflowClientType.SUBSCRIPTIONS;
    case WORKFLOW_CLIENT_TYPES.TRAINING:
      return WorkflowClientType.TRAINING;
    case WORKFLOW_CLIENT_TYPES.GOVERNANCE_CLOUD:
      return WorkflowClientType.GOVERNANCE_CLOUD;
    default:
      return WorkflowClientType.GOVERNANCE;
  }
};

export const executeWorkflowByName = async (
  {
    workflowName,
    payload,
    employeeName,
    employeeEmail,
    clientType = DEFAULT_WORKFLOW_CLIENT_TYPE,
  }: ExecuteWorkflowByNameParams,
  headers: Record<RequestMetadata, string>
): Promise<ExecuteWorkflowByNameResponse> => {
  const req = new ExecuteWorkflowByNameRequest({
    workflowName,
    payload,
    scEmployee: new SCEmployee({
      name: employeeName,
      email: employeeEmail,
    }),
    clientType: mapClientTypeToEnum(clientType),
  });

  return supportServiceConnectClient.executeWorkflowByName(req, {
    headers,
  });
};

type bulkCreateAccountDataParams = {
  region: Region;
  orgId: string;
  maxDaysInPast: number;
  templateIds: string[];
  totalNumInspections: number;
  issuesTitles: string[];
  totalNumIssues: number;
  actionsTitles: string[];
  totalNumActions: number;
  assetProfiles: AssetProfile[];
  assetReadingConfig?: AssetReadingConfig[];
  siteIds?: string[];
};

const mapOdometerType = (odometerType: Odometer) => {
  switch (odometerType) {
    case Odometer.ODOMETER_UNIT_KILOMETER:
      return MeasurementUnitSymbol.KILOMETERS;
    case Odometer.ODOMETER_UNIT_MILES:
      return MeasurementUnitSymbol.MILES;
    default:
      return MeasurementUnitSymbol.KILOMETERS;
  }
};

const mapUsage = (usageType: Usage) => {
  switch (usageType) {
    case Usage.HOUR:
      return MeasurementUnitSymbol.HOURS;
    case Usage.MINUTES:
      return MeasurementUnitSymbol.MINUTES;
    default:
      return MeasurementUnitSymbol.HOURS;
  }
};

export const bulkCreateDemoOrgData = async (
  {
    region,
    orgId,
    maxDaysInPast,
    templateIds,
    totalNumInspections,
    issuesTitles,
    totalNumIssues,
    actionsTitles,
    totalNumActions,
    assetProfiles,
    assetReadingConfig,
    siteIds,
  }: bulkCreateAccountDataParams,
  headers: Record<RequestMetadata, string>
): Promise<BulkCreateDemoOrgDataResponse> => {
  const totalNumInspectionNumber =
    typeof totalNumInspections === "string"
      ? Number(totalNumInspections)
      : totalNumInspections;
  const totalNumIssuesNumber =
    typeof totalNumInspections === "string"
      ? Number(totalNumIssues)
      : totalNumIssues;
  const totalNumActionsNumber =
    typeof totalNumInspections === "string"
      ? Number(totalNumActions)
      : totalNumActions;
  const req = new BulkCreateDemoOrgDataRequest({
    region: region,
    orgId: orgId,
    maxDaysInPast,
    siteIds: siteIds || [],
    inspectionsRequest: {
      templateIds: templateIds,
      totalNumInspections: totalNumInspectionNumber,
    },
    actionsRequest: {
      actionsTitles: actionsTitles,
      totalNumActions: totalNumActionsNumber,
    },
    issuesRequest: {
      issuesTitles: issuesTitles,
      totalNumIssues: totalNumIssuesNumber,
    },
    assetsRequest: {
      demoAssetConfigs: assetProfiles.map((asset) => {
        return {
          titles: asset.titles,
          assetTypeName: asset.typeName,
          prefixName: asset.prefix,
          includeOdometerReadings: asset.includeOdometer,
          odometerUnitSymbol: mapOdometerType(asset.odometer),
          includeUsageTime: asset.includeUsage,
          usageTimeUnitSymbol: mapUsage(asset.usage),
          numAssetsToCreate: asset.count,
        };
      }),
    } as BulkCreateDemoAssetsRequest,
    assetReadingsRequest: {
      assetReadingConfigs: assetReadingConfig
        ? assetReadingConfig.map((asset) => {
            return {
              assetId: asset.assetId,
              includeOdometerReadings: asset.includeOdometer,
              odometerUnitSymbol: mapOdometerType(asset.odometer),
              includeUsageTime: asset.includeUsage,
              usageTimeUnitSymbol: mapUsage(asset.usage),
            };
          })
        : [],
    } as BulkCreateDemoAssetReadingsRequest,
  });
  return supportServiceConnectClient.bulkCreateDemoOrgData(req, {
    headers,
  });
};

export const bulkAddUsersToGroups = async (
  req: BulkAddUsersToGroupsRequest,
  headers: Record<RequestMetadata, string>
): Promise<BulkAddUsersToGroupsResponse> => {
  return supportServiceConnectClient.bulkAddUsersToGroups(req, {
    headers,
  });
};

export const bulkTransferUsers = async (
  req: BulkTransferUsersRequest,
  headers: Record<RequestMetadata, string>
): Promise<BulkTransferUsersResponse> => {
  return supportServiceConnectClient.bulkTransferUsers(req, {
    headers,
  });
};

type BulkUpdateDocumentContextParams = {
  userId: string;
  region: Region;
  employeeName: string;
  employeeEmail: string;
  documentContextRequestReference: string;
};

export const bulkUpdateDocumentContext = async (
  {
    userId,
    region,
    employeeName,
    employeeEmail,
    documentContextRequestReference,
  }: BulkUpdateDocumentContextParams,
  headers: Record<RequestMetadata, string>
): Promise<BulkUpdateDocumentsContextResponse> => {
  const req = new BulkUpdateDocumentsContextRequest({
    userId,
    region,
    scEmployee: new SCEmployee({
      name: employeeName,
      email: employeeEmail,
    }),
    bulkDocumentContextRequestReference: documentContextRequestReference,
  });

  return supportServiceConnectClient.bulkUpdateDocumentsContext(req, {
    headers,
  });
};

type ResetOrgTemplatesAccessParams = {
  orgId: string;
  region: string;
  backupFolderName?: string;
};

export const resetOrgTemplatesAccess = async (
  { orgId, region, backupFolderName }: ResetOrgTemplatesAccessParams,
  headers: Record<RequestMetadata, string>
): Promise<ResetOrgTemplatesAccessResponse> => {
  const orgUUID = validateUUID(orgId) ? orgId : toUUID(orgId);
  const req = new ResetOrgTemplatesAccessRequest({
    orgId: orgUUID,
    region,
    backupFolderName: backupFolderName || "",
  });

  return supportServiceConnectClient.resetOrgTemplatesAccess(req, {
    headers,
  });
};

type ProvisionSupportUserParams = {
  email: string;
  firstName: string;
  lastName: string;
  targetOrgId: string;
  region: string;
  replacementUserId: string;
  reason?: string;
};

export const provisionSupportUser = async (
  {
    email,
    firstName,
    lastName,
    targetOrgId,
    region,
    replacementUserId,
    reason,
  }: ProvisionSupportUserParams,
  headers: Record<RequestMetadata, string>
): Promise<ProvisionSupportUserResponse> => {
  const req = new ProvisionSupportUserRequest({
    email,
    firstName,
    lastName,
    targetOrgId,
    region,
    replacementUserId,
  });

  if (reason) {
    req.reason = reason;
  }

  return supportServiceConnectClient.provisionSupportUser(req, {
    headers,
  });
};

export const bulkChangeEmailAddress = async (
  req: BulkChangeEmailAddressRequest,
  headers: Record<RequestMetadata, string>
): Promise<BulkChangeEmailAddressResponse> => {
  return supportServiceConnectClient.bulkChangeEmailAddress(req, {
    headers,
  });
};
